import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useBuildVersion } from '../hooks/useBuildVersion'
import { useUnsavedWork } from '../hooks/useUnsavedWork'

// Gets a tab that has been open across a deploy onto the new build, without ever showing the user
// anything. Renders nothing: a "new version available" notice is a request, and a warehouse terminal
// mid-shift is the last place anyone stops to answer one.
//
// Two moments, both chosen because the operator cannot be interrupted at them:
//
//   Navigating      a route change already discards the screen. Covers almost everyone — an
//                   operator changes screens constantly.
//   Tab goes away   nobody is looking. By the time they come back the app is already current,
//                   and they see one thing: the new version. This is the moment that covers the
//                   terminal parked on a single screen all shift and never navigating.
//
// Neither is unconditional. "The screen was being discarded anyway" is only true of the screen —
// a packing-proof recording, a write still in flight, a dirty form guarding unload from somewhere
// else in the tree all survive a route change and would not survive a reload. useUnsavedWork is
// the single place that knows about them; this component only asks.
export const StaleBundleGuard = () => {
  const { isStale, reload, checkNow } = useBuildVersion()
  const { hasUnsavedWork, forgetTyping } = useUnsavedWork()
  const location = useLocation()
  // The route we are on now, not the one we started on. Comparing against the mount-time route
  // would make navigating BACK to it read as "no navigation", which is the one case where a user
  // moving around the app would keep the old bundle.
  const lastPathRef = useRef(location.pathname)
  const inFlightRef = useRef(null)

  useEffect(() => {
    if (location.pathname === lastPathRef.current) return
    lastPathRef.current = location.pathname
    forgetTyping()
    if (isStale && !hasUnsavedWork()) reload('navigation')
  }, [isStale, location.pathname, reload, hasUnsavedWork, forgetTyping])

  useEffect(() => {
    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'hidden') {
        // Back already. Anything asked on the way out would now be answering into a tab
        // somebody is looking at, so it does not get to land.
        if (inFlightRef.current) inFlightRef.current.abort()
        return
      }
      if (hasUnsavedWork()) return

      // Checked here rather than trusting the last poll: the deploy may well have landed
      // while this tab was already in the background, in which case nothing has looked since.
      const controller = new AbortController()
      inFlightRef.current = controller
      const stale = await checkNow(controller.signal)

      // Every precondition re-read, because the round trip outlives them. A backgrounded tab
      // can be frozen with this fetch pending and resume only when the user returns to it, so
      // "nobody is looking" was true when the question was asked and says nothing about now.
      if (!stale || document.visibilityState !== 'hidden' || hasUnsavedWork()) return
      reload('hidden')
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      if (inFlightRef.current) inFlightRef.current.abort()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [checkNow, reload, hasUnsavedWork])

  return null
}
